import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Order, PatchOrderRequest } from "@tilt/pos-sdk";

import { getPosClient } from "../../lib/api";
import { parseAmountToCents } from "../../lib/format";
import { useFocusTrap } from "../../lib/use-focus-trap";
import { Alert } from "../ui/Alert";
import { Button } from "../ui/Button";
import { Field, TextArea, TextInput } from "../ui/Field";

/**
 * Edit-anytime dialog for an open order (#152).
 *
 * Surfaces only when ``order.status === "open"`` - partially-paid +
 * paid orders need supervisor PIN (#149); voided + refunded orders
 * are terminal. The API enforces the same rule, so this UI gate is
 * just a UX hint.
 *
 * Editable fields match the API's ``_PATCHABLE_FIELDS`` list:
 * external_reference, total_cents, description, customer_name /
 * phone / email. department_id + customer_id are out of scope here
 * - those need pickers, and the cashier can re-pick at order
 * creation time. Phase-2 surface lives with #145 (department
 * config) + a richer customer search.
 */
export function EditOrderDialog({
  order,
  onClose,
  onSaved,
}: {
  readonly order: Order;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reference, setReference] = useState(order.external_reference ?? "");
  const [description, setDescription] = useState(order.description ?? "");
  const [customerName, setCustomerName] = useState(order.customer_name ?? "");
  const [customerPhone, setCustomerPhone] = useState(order.customer_phone ?? "");
  const [customerEmail, setCustomerEmail] = useState(order.customer_email ?? "");
  const [amount, setAmount] = useState((order.total_cents / 100).toFixed(2));
  const dialogRef = useFocusTrap<HTMLDivElement>(true, onClose);

  const mutate = useMutation({
    mutationFn: async (): Promise<void> => {
      // Diff each field against the original - only send the keys
      // that actually changed. Empty string → null clears the column;
      // an unchanged value stays absent from the payload so the
      // server doesn't write it.
      const body: Record<string, unknown> = {};
      if (reference.trim() !== (order.external_reference ?? "").trim()) {
        body.external_reference = reference.trim() || null;
      }
      if (description.trim() !== (order.description ?? "").trim()) {
        body.description = description.trim() || null;
      }
      if (customerName.trim() !== (order.customer_name ?? "").trim()) {
        body.customer_name = customerName.trim() || null;
      }
      if (customerPhone.trim() !== (order.customer_phone ?? "").trim()) {
        body.customer_phone = customerPhone.trim() || null;
      }
      if (customerEmail.trim() !== (order.customer_email ?? "").trim()) {
        body.customer_email = customerEmail.trim() || null;
      }
      const newTotal = parseAmountToCents(amount);
      if (newTotal !== null && newTotal !== order.total_cents && newTotal > 0) {
        body.total_cents = newTotal;
      }
      if (Object.keys(body).length === 0) {
        return;
      }
      await getPosClient().patchOrder(order.id, body as PatchOrderRequest);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["orders", order.id] });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      onSaved();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-order-title"
        className="w-full max-w-lg space-y-4 rounded-lg bg-surface p-5 shadow-xl"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 id="edit-order-title" className="text-base font-semibold text-strong">
              {t("orderDetail.editTitle")}
            </h3>
            <p className="mt-0.5 text-xs text-muted">{t("orderDetail.editSubtitle")}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.cancel")}
            className="rounded p-1 text-faint hover:bg-surface-strong hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t("sale.reference")}>
            <TextInput
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              maxLength={128}
            />
          </Field>
          <Field label={t("sale.amountDue")}>
            <TextInput
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              maxLength={16}
            />
          </Field>
          <Field label={t("sale.customerName")} className="sm:col-span-2">
            <TextInput
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              maxLength={200}
            />
          </Field>
          <Field label={t("sale.customerPhone")}>
            <TextInput
              type="tel"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              maxLength={64}
            />
          </Field>
          <Field label={t("sale.customerEmail")}>
            <TextInput
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              maxLength={320}
            />
          </Field>
          <Field label={t("sale.description")} className="sm:col-span-2">
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
            />
          </Field>
        </div>

        {mutate.isError && (
          <Alert tone="error">{(mutate.error as Error).message ?? t("common.tryAgain")}</Alert>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => mutate.mutate()} loading={mutate.isPending}>
            {t("common.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
